import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useAnalytics} from '@github-ui/use-analytics'
import {type ColorName, isColorName, useNamedColor} from '@github-ui/use-named-color'
import {CheckIcon, IssueClosedIcon, IssueOpenedIcon, KebabHorizontalIcon, SkipIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, AnchoredOverlay, Button, IconButton, Label, Link} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {
  type ApplySuggestionRef,
  commitApplyPendingIssueSuggestionsMutation,
} from '../../mutations/apply-pending-issue-suggestions-mutation'
import {
  commitRejectPendingIssueSuggestionsMutation,
  type RejectSuggestionRef,
} from '../../mutations/reject-pending-issue-suggestions-mutation'
import type {useRecentAgentActivity_issue$key} from './__generated__/useRecentAgentActivity_issue.graphql'
import styles from './RecentAgentActivity.module.css'
import {useDismissRecentAgentActivity} from './use-dismiss-recent-agent-activity'
import {useHoverPopover} from './use-hover-popover'
import type {
  CloseStateReason,
  DuplicateTarget,
  PendingSuggestion,
  SuggestionConfidence,
} from './use-recent-agent-activity'
import {useRecentAgentActivity} from './use-recent-agent-activity'
import {useRecentAgentActivitySubscription} from './use-recent-agent-activity-subscription'

function resolveDotColor(color: string | null | undefined): string | undefined {
  if (!color) return undefined
  // Hex string with or without leading '#'
  if (/^#?[0-9a-f]{3,8}$/i.test(color)) {
    return color.startsWith('#') ? color : `#${color}`
  }
  return undefined
}

type LabelColorDotProps = {
  color?: string | null
}

function LabelColorDot({color}: LabelColorDotProps) {
  const isNamed = Boolean(color && isColorName(color))
  const {accent} = useNamedColor(isNamed ? (color as ColorName) : undefined)
  const resolved = isNamed ? accent : resolveDotColor(color)
  if (!resolved) return null
  return <span className={styles.chipDot} style={{backgroundColor: resolved}} aria-hidden="true" />
}

type ChipValueProps = {
  name: string
  color?: string | null
}

function ChipValue({name, color}: ChipValueProps) {
  return (
    <span className={styles.chipValueInner}>
      <LabelColorDot color={color} />
      <span className={styles.chipValueText}>{name}</span>
    </span>
  )
}

type CloseChipValueProps = {
  stateReason: CloseStateReason
  duplicateOf: DuplicateTarget | null
}

function CloseChipValue({stateReason, duplicateOf}: CloseChipValueProps) {
  if (stateReason === 'COMPLETED') {
    return (
      <span className={styles.chipValueInner}>
        <IssueClosedIcon size={12} className={styles.chipCloseIconDone} aria-hidden="true" />
        <span className={styles.chipValueText}>completed</span>
      </span>
    )
  }
  if (stateReason === 'NOT_PLANNED') {
    return (
      <span className={styles.chipValueInner}>
        <SkipIcon size={12} className={styles.chipCloseIconMuted} aria-hidden="true" />
        <span className={styles.chipValueText}>not planned</span>
      </span>
    )
  }
  // DUPLICATE — only reachable when `duplicateOf` is present (processor drops it otherwise).
  if (!duplicateOf) return null
  return (
    <span className={styles.chipValueInner}>
      <span className={styles.chipValueText}>duplicate of</span>
      <IssueOpenedIcon size={12} className={styles.chipCloseIconOpen} aria-hidden="true" />
      <span className={styles.chipValueText}>#{duplicateOf.number}</span>
    </span>
  )
}

function renderChipValue(suggestion: PendingSuggestion): ReactNode {
  if (suggestion.type === 'issue_closed' && suggestion.closeStateReason) {
    return <CloseChipValue stateReason={suggestion.closeStateReason} duplicateOf={suggestion.duplicateOf} />
  }
  return <ChipValue name={suggestion.value ?? ''} color={suggestion.color} />
}

// Builds the rationale popover body for a close suggestion. Duplicate suggestions
// get a link to the target issue/PR above the free-form rationale so reviewers can
// open it in a new tab without leaving the panel.
function renderCloseRationale(suggestion: PendingSuggestion): ReactNode {
  const {duplicateOf, closeStateReason, rationale} = suggestion
  if (closeStateReason === 'DUPLICATE' && duplicateOf) {
    return (
      <>
        <div className={styles.duplicateTarget}>
          <span>Duplicate of </span>
          <Link href={duplicateOf.url} inline>
            {duplicateOf.repoNameWithOwner}#{duplicateOf.number}
          </Link>
          <span className={styles.duplicateTargetTitle}>{duplicateOf.title}</span>
        </div>
        {rationale ? <div>{rationale}</div> : null}
      </>
    )
  }
  return rationale ?? null
}

function renderRationaleNode(suggestion: PendingSuggestion): {node: ReactNode; length: number} {
  if (suggestion.type === 'issue_closed') {
    const node = renderCloseRationale(suggestion)
    const length =
      (suggestion.rationale?.length ?? 0) +
      (suggestion.closeStateReason === 'DUPLICATE' && suggestion.duplicateOf ? suggestion.duplicateOf.title.length : 0)
    return {node, length}
  }
  return {node: suggestion.rationale ?? null, length: suggestion.rationale?.length ?? 0}
}

function closeChipAriaValue(suggestion: PendingSuggestion): string {
  if (suggestion.closeStateReason === 'COMPLETED') return 'completed'
  if (suggestion.closeStateReason === 'NOT_PLANNED') return 'not planned'
  if (suggestion.closeStateReason === 'DUPLICATE' && suggestion.duplicateOf) {
    return `duplicate of ${suggestion.duplicateOf.repoNameWithOwner}#${suggestion.duplicateOf.number}`
  }
  return ''
}

type SuggestionChipProps = {
  fieldName: string
  applied: boolean
  rationale?: ReactNode
  // Length used for analytics. Provide explicitly when `rationale` is a node.
  rationaleLength?: number
  ariaLabel: string
  confidence?: SuggestionConfidence | null
  onApprove?: () => void
  onReject?: () => void
  children: ReactNode
  wrapperClassName?: string
  onWrapperTransitionEnd?: (event: React.TransitionEvent<HTMLDivElement>) => void
}

const CONFIDENCE_LABELS: Record<SuggestionConfidence, string> = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
}

const CONFIDENCE_LABEL_VARIANTS: Record<SuggestionConfidence, 'success' | 'accent' | 'done'> = {
  HIGH: 'success',
  MEDIUM: 'accent',
  LOW: 'done',
}

function SuggestionChip({
  fieldName,
  applied,
  rationale,
  rationaleLength,
  ariaLabel,
  confidence,
  onApprove,
  onReject,
  children,
  wrapperClassName,
  onWrapperTransitionEnd,
}: SuggestionChipProps) {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const {sendAnalyticsEvent} = useAnalytics()

  const interactive = Boolean(onApprove || onReject || rationale)

  const handleFirstOpen = useCallback(() => {
    const length = rationaleLength ?? (typeof rationale === 'string' ? rationale.length : null)
    if (length !== null && length > 0) {
      sendAnalyticsEvent('recent_agent_actions_panel.rationale_clicked', 'RECENT_AGENT_ACTIVITY', {
        rationaleLength: length,
      })
    }
  }, [rationale, rationaleLength, sendAnalyticsEvent])

  const {open, anchorRegionProps, anchorProps, contentProps, closeNow} = useHoverPopover({
    onFirstOpen: handleFirstOpen,
  })

  const handleApproveClick = useCallback(() => {
    closeNow()
    onApprove?.()
  }, [closeNow, onApprove])

  const handleRejectClick = useCallback(() => {
    closeNow()
    onReject?.()
  }, [closeNow, onReject])

  const chipContent = (
    <>
      <span className={styles.chipFieldName}>{fieldName}</span>
      <span className={styles.chipArrow} aria-hidden="true">
        →
      </span>
      <span className={styles.chipValue}>{children}</span>
      {applied && <CheckIcon size={12} className={styles.chipCheck} aria-hidden="true" />}
    </>
  )

  if (!interactive) {
    return (
      <div
        className={clsx(styles.chip, applied && styles.chipApplied)}
        data-confidence={confidence ?? undefined}
        role="listitem"
      >
        {chipContent}
      </div>
    )
  }

  return (
    <div
      {...anchorRegionProps}
      role="listitem"
      className={clsx(styles.chipWrapper, wrapperClassName)}
      onTransitionEnd={onWrapperTransitionEnd}
    >
      <button
        {...anchorProps}
        ref={anchorRef}
        type="button"
        className={clsx(styles.chip, styles.chipButton, applied && styles.chipApplied)}
        data-confidence={confidence ?? undefined}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {chipContent}
      </button>
      {open && (
        <AnchoredOverlay
          open={open}
          anchorRef={anchorRef}
          renderAnchor={null}
          onClose={closeNow}
          // Don't trap focus on open: the overlay is hover/focus-triggered, so
          // pulling focus into it would be disruptive. Keyboard users can still
          // Tab into the rationale actions from the chip button.
          focusTrapSettings={{disabled: true}}
        >
          <div {...contentProps} className={styles.rationaleOverlay}>
            {confidence && (
              <Label variant={CONFIDENCE_LABEL_VARIANTS[confidence]} className={styles.confidenceLabel}>
                {`${CONFIDENCE_LABELS[confidence]} confidence`}
              </Label>
            )}
            {rationale ? <div className={styles.rationaleBody}>{rationale}</div> : null}
            {(onApprove || onReject) && (
              <div className={styles.rationaleActions}>
                {onReject && (
                  <Button variant="default" size="small" onClick={handleRejectClick}>
                    Decline
                  </Button>
                )}
                {onApprove && (
                  <Button variant="primary" size="small" onClick={handleApproveClick}>
                    Accept
                  </Button>
                )}
              </div>
            )}
          </div>
        </AnchoredOverlay>
      )}
    </div>
  )
}

type AppliedSuggestionChipProps = {
  suggestion: PendingSuggestion
}

// Renders a recently-approved suggestion as a non-interactive chip with a
// checkmark. This is a transient, optimistic-only state: the panel queries
// only `pendingSuggestions`, so on refresh the applied row disappears.
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
const AppliedSuggestionChip = memo(function AppliedSuggestionChip({suggestion}: AppliedSuggestionChipProps) {
  const valueLabel = suggestion.type === 'issue_closed' ? closeChipAriaValue(suggestion) : (suggestion.value ?? '')
  const {node: rationaleNode, length: rationaleLength} = renderRationaleNode(suggestion)
  return (
    <SuggestionChip
      fieldName={suggestion.displayFieldName}
      applied
      rationale={rationaleNode}
      rationaleLength={rationaleLength}
      ariaLabel={`Applied ${suggestion.displayFieldName.toLowerCase()}: ${valueLabel}${
        rationaleNode ? '. View rationale' : ''
      }`}
      confidence={suggestion.confidence}
    >
      {renderChipValue(suggestion)}
    </SuggestionChip>
  )
})

type PendingSuggestionChipProps = {
  suggestion: PendingSuggestion
  onApprove: (suggestion: PendingSuggestion) => void
  onReject: (suggestion: PendingSuggestion) => void
}

const REJECT_ANIMATION_FALLBACK_MS = 400

// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
const PendingSuggestionChip = memo(function PendingSuggestionChip({
  suggestion,
  onApprove,
  onReject,
}: PendingSuggestionChipProps) {
  const [isRejecting, setIsRejecting] = useState(false)
  const hasFiredRejectRef = useRef(false)
  const fallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fireReject = useCallback(() => {
    if (hasFiredRejectRef.current) return
    hasFiredRejectRef.current = true
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current)
      fallbackTimeoutRef.current = null
    }
    onReject(suggestion)
  }, [onReject, suggestion])

  useEffect(
    () => () => {
      if (fallbackTimeoutRef.current) clearTimeout(fallbackTimeoutRef.current)
    },
    [],
  )

  const handleApprove = useCallback(() => onApprove(suggestion), [onApprove, suggestion])
  const handleReject = useCallback(() => {
    setIsRejecting(true)
    // The mutation is normally fired from transitionEnd so the chip animates
    // out first. transitionEnd is not guaranteed to fire (reduced motion,
    // backgrounded tabs, rapid DOM changes), so add a timeout fallback that
    // still fires the reject if the event never arrives.
    fallbackTimeoutRef.current = setTimeout(fireReject, REJECT_ANIMATION_FALLBACK_MS)
  }, [fireReject])

  const handleTransitionEnd = useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (isRejecting && event.propertyName === 'opacity' && event.target === event.currentTarget) {
        fireReject()
      }
    },
    [fireReject, isRejecting],
  )

  const ariaValue = suggestion.type === 'issue_closed' ? closeChipAriaValue(suggestion) : (suggestion.value ?? '')
  const {node: rationaleNode, length: rationaleLength} = renderRationaleNode(suggestion)

  return (
    <SuggestionChip
      fieldName={suggestion.displayFieldName}
      applied={false}
      rationale={rationaleNode}
      rationaleLength={rationaleLength}
      confidence={suggestion.confidence}
      ariaLabel={`Pending ${suggestion.displayFieldName} suggestion${ariaValue ? `: ${ariaValue}` : ''}${suggestion.confidence ? `. ${CONFIDENCE_LABELS[suggestion.confidence]} confidence` : ''}. View details`}
      onApprove={handleApprove}
      onReject={handleReject}
      wrapperClassName={isRejecting ? styles.chipRejecting : undefined}
      onWrapperTransitionEnd={handleTransitionEnd}
    >
      {renderChipValue(suggestion)}
    </SuggestionChip>
  )
})

type PendingLabelGroupChipProps = {
  suggestions: PendingSuggestion[]
  onApprove: (suggestions: PendingSuggestion[]) => void
  onReject: (suggestions: PendingSuggestion[]) => void
}

// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
const PendingLabelGroupChip = memo(function PendingLabelGroupChip({
  suggestions,
  onApprove,
  onReject,
}: PendingLabelGroupChipProps) {
  const [isRejecting, setIsRejecting] = useState(false)
  const hasFiredRejectRef = useRef(false)
  const fallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fireReject = useCallback(() => {
    if (hasFiredRejectRef.current) return
    hasFiredRejectRef.current = true
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current)
      fallbackTimeoutRef.current = null
    }
    onReject(suggestions)
  }, [onReject, suggestions])

  useEffect(
    () => () => {
      if (fallbackTimeoutRef.current) clearTimeout(fallbackTimeoutRef.current)
    },
    [],
  )

  const handleApprove = useCallback(() => onApprove(suggestions), [onApprove, suggestions])
  const handleReject = useCallback(() => {
    setIsRejecting(true)
    fallbackTimeoutRef.current = setTimeout(fireReject, REJECT_ANIMATION_FALLBACK_MS)
  }, [fireReject])

  const handleTransitionEnd = useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (isRejecting && event.propertyName === 'opacity' && event.target === event.currentTarget) {
        fireReject()
      }
    },
    [fireReject, isRejecting],
  )

  const confidence = suggestions[0]?.confidence ?? null
  const labelNames = suggestions.map(s => s.value ?? '').join(', ')
  const totalRationaleLength = suggestions.reduce((sum, s) => sum + (s.rationale?.length ?? 0), 0)
  const hasAnyRationale = suggestions.some(s => Boolean(s.rationale))
  const rationaleNode = hasAnyRationale ? (
    <ul className={styles.rationaleList}>
      {suggestions.map(s => (
        <li key={s.labelId ?? s.value}>
          <span className={styles.rationaleListLabel}>
            <LabelColorDot color={s.color} />
            {s.value}
          </span>
          {s.rationale ? <>: {s.rationale}</> : null}
        </li>
      ))}
    </ul>
  ) : undefined

  return (
    <SuggestionChip
      fieldName="Labels"
      applied={false}
      rationale={rationaleNode}
      rationaleLength={totalRationaleLength}
      confidence={confidence}
      ariaLabel={`Pending label suggestions: ${labelNames}${confidence ? `. ${CONFIDENCE_LABELS[confidence]} confidence` : ''}. View details`}
      onApprove={handleApprove}
      onReject={handleReject}
      wrapperClassName={isRejecting ? styles.chipRejecting : undefined}
      onWrapperTransitionEnd={handleTransitionEnd}
    >
      {suggestions.map((s, index) => (
        <span key={s.labelId ?? `${s.value}-${index}`} className={styles.chipValueGroup}>
          {index > 0 && (
            <span className={styles.chipValueSeparator} aria-hidden="true">
              ,
            </span>
          )}
          <ChipValue name={s.value ?? ''} color={s.color} />
        </span>
      ))}
    </SuggestionChip>
  )
})

type PendingChipItem =
  | {kind: 'single'; suggestion: PendingSuggestion}
  | {kind: 'label-group'; key: string; suggestions: PendingSuggestion[]}

// Group consecutive non-grouping suggestions individually, but bucket label
// suggestions that share the same actor and confidence into a single chip so
// the user can review and approve/reject them together.
function buildPendingChipItems(suggestions: PendingSuggestion[]): PendingChipItem[] {
  const buckets = new Map<string, PendingSuggestion[]>()
  type Slot = {kind: 'single'; suggestion: PendingSuggestion} | {kind: 'bucket'; key: string}
  const slots: Slot[] = []

  for (const suggestion of suggestions) {
    if (suggestion.type === 'label_added' && suggestion.confidence) {
      const key = `${suggestion.actorId}|${suggestion.confidence}`
      let bucket = buckets.get(key)
      if (!bucket) {
        bucket = []
        buckets.set(key, bucket)
        slots.push({kind: 'bucket', key})
      }
      bucket.push(suggestion)
    } else {
      slots.push({kind: 'single', suggestion})
    }
  }

  return slots.map(slot => {
    if (slot.kind === 'single') return slot
    const bucket = buckets.get(slot.key) ?? []
    if (bucket.length === 1 && bucket[0]) {
      return {kind: 'single', suggestion: bucket[0]}
    }
    return {kind: 'label-group', key: slot.key, suggestions: bucket}
  })
}

type RecentAgentActivityProps = {
  issue: useRecentAgentActivity_issue$key
  issueBodyRef: React.RefObject<HTMLDivElement | null>
}

const FEEDBACK_URL = 'https://github.com/orgs/community/discussions/198815'

function buildApplyRef(suggestion: PendingSuggestion): ApplySuggestionRef | null {
  if (suggestion.type === 'type_set' && suggestion.issueTypeId) {
    return {kind: 'TYPE', issueTypeId: suggestion.issueTypeId}
  }
  if (suggestion.type === 'label_added' && suggestion.labelId) {
    return {kind: 'LABEL', labelId: suggestion.labelId}
  }
  if (suggestion.type === 'field_set' && suggestion.issueFieldId) {
    return {kind: 'FIELD', issueFieldId: suggestion.issueFieldId}
  }
  if (suggestion.type === 'issue_closed') {
    return {kind: 'CLOSE'}
  }
  return null
}

function buildRejectRef(suggestion: PendingSuggestion): RejectSuggestionRef | null {
  if (suggestion.type === 'type_set' && suggestion.issueTypeId) {
    return {kind: 'TYPE', issueTypeId: suggestion.issueTypeId}
  }
  if (suggestion.type === 'label_added' && suggestion.labelId) {
    return {kind: 'LABEL', labelId: suggestion.labelId}
  }
  if (suggestion.type === 'field_set' && suggestion.issueFieldId) {
    return {kind: 'FIELD', issueFieldId: suggestion.issueFieldId}
  }
  if (suggestion.type === 'issue_closed') {
    return {kind: 'CLOSE'}
  }
  return null
}

function countByConfidence(suggestions: PendingSuggestion[]): {
  high_confidence_count: number
  medium_confidence_count: number
  low_confidence_count: number
  no_confidence_count: number
} {
  const counts = {high_confidence_count: 0, medium_confidence_count: 0, low_confidence_count: 0, no_confidence_count: 0}
  for (const suggestion of suggestions) {
    switch (suggestion.confidence) {
      case 'HIGH':
        counts.high_confidence_count++
        break
      case 'MEDIUM':
        counts.medium_confidence_count++
        break
      case 'LOW':
        counts.low_confidence_count++
        break
      default:
        counts.no_confidence_count++
    }
  }
  return counts
}

export function RecentAgentActivity({issue, issueBodyRef}: RecentAgentActivityProps) {
  const {viewerCanUpdateMetadata, issueId, pendingSuggestions} = useRecentAgentActivity(issue)
  const {isDismissed, dismissForIssue, dismissForever} = useDismissRecentAgentActivity(issueId)
  const {sendAnalyticsEvent} = useAnalytics()
  const environment = useRelayEnvironment()

  useRecentAgentActivitySubscription(issueId)

  const pendingSuggestionRows = useMemo(
    () => pendingSuggestions.groups.flatMap(group => group.suggestions.filter(s => s.state === 'PENDING')),
    [pendingSuggestions.groups],
  )
  const pendingCount = pendingSuggestionRows.length

  // Suggestions the viewer just approved in this session. Rendered as transient
  // applied chips so the viewer immediately sees the result of their action.
  // Cleared on unmount/refresh; the server's `pendingSuggestions` field only
  // returns PENDING rows (filtered in the hook), so applied chips never persist.
  const [recentlyApproved, setRecentlyApproved] = useState<PendingSuggestion[]>([])
  const appliedCount = recentlyApproved.length

  const pendingChipItems = useMemo(() => buildPendingChipItems(pendingSuggestionRows), [pendingSuggestionRows])

  const panelRef = useRef<HTMLDivElement | null>(null)

  // After a chip is approved or rejected (individually or in bulk), move focus
  // to the next focusable chip in the list. If none remain, fall back to the
  // kebab "More actions" button, then to the issue body if the whole panel
  // has unmounted, so focus doesn't fall to document.body.
  const focusAfterAction = useCallback(() => {
    // Defer two frames so React can unmount the dismissed chip and any
    // optimistic store update can land.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const panel = panelRef.current
        if (!panel) {
          issueBodyRef.current?.focus()
          return
        }
        const nextChip = panel.querySelector<HTMLButtonElement>('[role="list"] button')
        if (nextChip) {
          nextChip.focus()
          return
        }
        const fallback = panel.querySelector<HTMLButtonElement>('[aria-label="More actions"]')
        fallback?.focus()
      })
    })
  }, [issueBodyRef])

  const handleDismissForIssue = useCallback(() => {
    dismissForIssue()
    focusAfterAction()
  }, [dismissForIssue, focusAfterAction])

  const handleDismissForever = useCallback(() => {
    dismissForever()
    focusAfterAction()
  }, [dismissForever, focusAfterAction])

  const handleApprove = useCallback(
    (suggestion: PendingSuggestion) => {
      sendAnalyticsEvent('issues.recent_agent_activity.suggestion_approved', 'RECENT_AGENT_ACTIVITY', {
        suggestion_type: suggestion.type,
        confidence: suggestion.confidence ?? 'NONE',
        intent_id: suggestion.intentId ?? 'NONE',
      })
      const ref = buildApplyRef(suggestion)
      if (!ref) return
      commitApplyPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId: suggestion.actorId,
        suggestions: [ref],
      })
      setRecentlyApproved(prev => [...prev, suggestion])
      announce('Suggestion approved')
      focusAfterAction()
    },
    [environment, issueId, sendAnalyticsEvent, focusAfterAction],
  )

  const handleReject = useCallback(
    (suggestion: PendingSuggestion) => {
      sendAnalyticsEvent('issues.recent_agent_activity.suggestion_rejected', 'RECENT_AGENT_ACTIVITY', {
        suggestion_type: suggestion.type,
        confidence: suggestion.confidence ?? 'NONE',
        intent_id: suggestion.intentId ?? 'NONE',
      })
      const ref = buildRejectRef(suggestion)
      if (!ref) return
      commitRejectPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId: suggestion.actorId,
        suggestions: [ref],
      })
      announce('Suggestion rejected')
      focusAfterAction()
    },
    [environment, issueId, sendAnalyticsEvent, focusAfterAction],
  )

  // Approve a group of pending suggestions that share an actor (e.g. multiple
  // labels suggested at the same confidence). We fire one mutation with all
  // the refs and an analytics event per suggestion so per-suggestion metrics
  // remain consistent with individual approvals.
  const handleApproveGroup = useCallback(
    (suggestionsToApprove: PendingSuggestion[]) => {
      if (suggestionsToApprove.length === 0) return
      const refs: ApplySuggestionRef[] = []
      const approved: PendingSuggestion[] = []
      for (const suggestion of suggestionsToApprove) {
        sendAnalyticsEvent('issues.recent_agent_activity.suggestion_approved', 'RECENT_AGENT_ACTIVITY', {
          suggestion_type: suggestion.type,
          confidence: suggestion.confidence ?? 'NONE',
          intent_id: suggestion.intentId ?? 'NONE',
        })
        const ref = buildApplyRef(suggestion)
        if (ref) {
          refs.push(ref)
          approved.push(suggestion)
        }
      }
      if (refs.length === 0) return
      commitApplyPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId: suggestionsToApprove[0]!.actorId,
        suggestions: refs,
      })
      setRecentlyApproved(prev => [...prev, ...approved])
      announce(refs.length === 1 ? 'Suggestion approved' : `${refs.length} suggestions approved`)
      focusAfterAction()
    },
    [environment, issueId, sendAnalyticsEvent, focusAfterAction],
  )

  const handleRejectGroup = useCallback(
    (suggestionsToReject: PendingSuggestion[]) => {
      if (suggestionsToReject.length === 0) return
      const refs: RejectSuggestionRef[] = []
      for (const suggestion of suggestionsToReject) {
        sendAnalyticsEvent('issues.recent_agent_activity.suggestion_rejected', 'RECENT_AGENT_ACTIVITY', {
          suggestion_type: suggestion.type,
          confidence: suggestion.confidence ?? 'NONE',
          intent_id: suggestion.intentId ?? 'NONE',
        })
        const ref = buildRejectRef(suggestion)
        if (ref) refs.push(ref)
      }
      if (refs.length === 0) return
      commitRejectPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId: suggestionsToReject[0]!.actorId,
        suggestions: refs,
      })
      announce(refs.length === 1 ? 'Suggestion rejected' : `${refs.length} suggestions rejected`)
      focusAfterAction()
    },
    [environment, issueId, sendAnalyticsEvent, focusAfterAction],
  )

  const handleApproveAll = useCallback(() => {
    sendAnalyticsEvent('issues.recent_agent_activity.suggestion_bulk_approved', 'RECENT_AGENT_ACTIVITY', {
      suggestion_count: pendingSuggestionRows.length,
      intent_ids: pendingSuggestionRows.map(s => s.intentId ?? 'NONE'),
      ...countByConfidence(pendingSuggestionRows),
    })
    // The unified mutation requires a single actorId per call; batch suggestions per actor.
    const byActor = new Map<string, {refs: ApplySuggestionRef[]; approved: PendingSuggestion[]}>()
    for (const suggestion of pendingSuggestionRows) {
      const ref = buildApplyRef(suggestion)
      if (!ref) continue
      let bucket = byActor.get(suggestion.actorId)
      if (!bucket) {
        bucket = {refs: [], approved: []}
        byActor.set(suggestion.actorId, bucket)
      }
      bucket.refs.push(ref)
      bucket.approved.push(suggestion)
    }
    const approvedAll: PendingSuggestion[] = []
    for (const [actorId, {refs, approved}] of byActor) {
      commitApplyPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId,
        suggestions: refs,
      })
      approvedAll.push(...approved)
    }
    if (approvedAll.length > 0) setRecentlyApproved(prev => [...prev, ...approvedAll])
    const count = pendingSuggestionRows.length
    announce(count === 1 ? 'Suggestion approved' : `${count} suggestions approved`)
    focusAfterAction()
  }, [pendingSuggestionRows, environment, issueId, sendAnalyticsEvent, focusAfterAction])

  const handleRejectAll = useCallback(() => {
    sendAnalyticsEvent('issues.recent_agent_activity.suggestion_bulk_rejected', 'RECENT_AGENT_ACTIVITY', {
      suggestion_count: pendingSuggestionRows.length,
      intent_ids: pendingSuggestionRows.map(s => s.intentId ?? 'NONE'),
      ...countByConfidence(pendingSuggestionRows),
    })
    // The unified mutation requires a single actorId per call; batch suggestions per actor.
    const byActor = new Map<string, {refs: RejectSuggestionRef[]}>()
    for (const suggestion of pendingSuggestionRows) {
      const ref = buildRejectRef(suggestion)
      if (!ref) continue
      let bucket = byActor.get(suggestion.actorId)
      if (!bucket) {
        bucket = {refs: []}
        byActor.set(suggestion.actorId, bucket)
      }
      bucket.refs.push(ref)
    }
    for (const [actorId, {refs}] of byActor) {
      commitRejectPendingIssueSuggestionsMutation({
        environment,
        issueId,
        actorId,
        suggestions: refs,
      })
    }
    const count = pendingSuggestionRows.length
    announce(count === 1 ? 'Suggestion rejected' : `${count} suggestions rejected`)
    focusAfterAction()
  }, [pendingSuggestionRows, environment, issueId, sendAnalyticsEvent, focusAfterAction])

  const hasFiredDisplayedRef = useRef(false)
  useEffect(() => {
    if (pendingCount > 0 && viewerCanUpdateMetadata && !isDismissed && !hasFiredDisplayedRef.current) {
      hasFiredDisplayedRef.current = true
      sendAnalyticsEvent('recent_agent_actions_panel.displayed', 'RECENT_AGENT_ACTIVITY', {
        action_count: pendingCount,
        agent_count: pendingSuggestions.groups.length,
      })
    }
  }, [pendingCount, viewerCanUpdateMetadata, isDismissed, sendAnalyticsEvent, pendingSuggestions.groups.length])

  if (pendingCount === 0 && appliedCount === 0) return null
  if (!viewerCanUpdateMetadata || isDismissed) return null

  // Require at least one identifiable agent — either from a still-pending group
  // or from a suggestion the viewer just approved (whose group has been
  // filtered out of `pendingSuggestions` once it transitioned to APPLIED).
  const hasAgent = pendingSuggestions.groups.some(g => g.agentSlug) || recentlyApproved.some(s => s.actorLogin)
  if (!hasAgent) return null

  const hasPending = pendingCount > 0

  return (
    <div
      ref={panelRef}
      className={clsx(styles.container, isFeatureEnabled('issue_inline_avatars') && styles.container_inlineAvatars)}
      data-testid="recent-agent-activity"
    >
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <h2 className={styles.title}>Agent suggestions</h2>
          <Link href={FEEDBACK_URL} target="_blank" rel="noopener noreferrer" className={styles.previewLink}>
            <Label variant="secondary">Private preview</Label>
          </Link>
        </div>
        <div className={styles.headerActions}>
          {hasPending && (
            <>
              <Button variant="default" size="small" onClick={handleRejectAll}>
                Decline all
              </Button>
              <Button variant="primary" size="small" onClick={handleApproveAll}>
                Accept all
              </Button>
            </>
          )}
          <ActionMenu>
            <ActionMenu.Anchor>
              <IconButton icon={KebabHorizontalIcon} aria-label="More actions" size="small" variant="invisible" />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay>
              <ActionList>
                <ActionList.LinkItem href={FEEDBACK_URL} target="_blank" rel="noopener noreferrer">
                  Give feedback
                </ActionList.LinkItem>
                <ActionList.Item onSelect={handleDismissForIssue}>Dismiss</ActionList.Item>
                <ActionList.Item onSelect={handleDismissForever}>Never show this again</ActionList.Item>
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </div>
      </div>
      <div className={styles.chips} role="list" aria-label="Agent suggestions">
        {pendingChipItems.map(item => {
          if (item.kind === 'label-group') {
            return (
              <PendingLabelGroupChip
                key={`pending-group-${item.key}`}
                suggestions={item.suggestions}
                onApprove={handleApproveGroup}
                onReject={handleRejectGroup}
              />
            )
          }
          const {suggestion} = item
          const subjectKey =
            suggestion.issueTypeId ??
            suggestion.labelId ??
            suggestion.issueFieldId ??
            `${suggestion.type}:${suggestion.displayFieldName}`
          return (
            <PendingSuggestionChip
              key={`pending-${suggestion.actorId}:${subjectKey}`}
              suggestion={suggestion}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          )
        })}
        {hasPending && appliedCount > 0 && <span className={styles.divider} aria-hidden="true" />}
        {recentlyApproved.map(suggestion => {
          const subjectKey =
            suggestion.issueTypeId ??
            suggestion.labelId ??
            suggestion.issueFieldId ??
            `${suggestion.type}:${suggestion.displayFieldName}`
          return <AppliedSuggestionChip key={`applied-${suggestion.actorId}:${subjectKey}`} suggestion={suggestion} />
        })}
      </div>
    </div>
  )
}
